import PropTypes from 'prop-types';
import React from 'react';
import ImmutablePropTypes from 'react-immutable-proptypes';
import { FormattedMessage, injectIntl } from 'react-intl';

import { bindAll } from '@bibliocommons/utils-react';
import { bibShape, ugcContentAdvisoryShape } from '@bibliocommons/bc-prop-types';

import shelfItemShape from 'app/shapes/shelves/shelfItemShape';
import BaseUGCEditor from '../BaseUGCEditor';
import ContentAdvisoryField from '../../ContentAdvisoryField';

import './ContentAdvisoryUGCEditor.scss';

const DETAILS_ERROR_KEY = 'details';

export const contentAdvisoryTypes = [
  'offensive_language',
  'graphic_violence',
  'sexual_content',
  'frightening_scenes',
  'other'
];

function getDefaultFieldState() {
  return {
    isNewEntry: true,
    isChecked: false,
    initialIsChecked: false,
    initialValue: '',
    value: '',
    detailsError: null
  };
}

function noticeHasBeenAltered(notice) {
  return notice.isChecked !== notice.initialIsChecked || notice.value !== notice.initialValue;
}

export class ContentAdvisoryUGCEditor extends React.PureComponent {
  constructor(props) {
    super(props);
    bindAll(this);
    this.state = {};
    contentAdvisoryTypes.forEach(type => {
      this.state[type] = getDefaultFieldState();
    });

    const { existingAdvisories } = this.props;
    if (existingAdvisories) {
      existingAdvisories.forEach(notice => {
        this.state[notice.get('type')] = {
          isNewEntry: false,
          isChecked: true,
          initialIsChecked: true,
          initialValue: notice.get('notice'),
          value: notice.get('notice')
        };
      });
    }
  }

  isFormValid() {
    return contentAdvisoryTypes.every(type => !this.state[type].detailsError);
  }

  handleSave(callback) {
    const { existingAdvisories, contentAdvisoriesActions, bib } = this.props;
    const postSuccess = callback;
    const batchPost = [];
    const batchUpdate = [];
    const batchDelete = [];
    const errorState = {};

    contentAdvisoryTypes.forEach(type => {
      const noticeState = this.state[type];

      if (noticeHasBeenAltered(noticeState)) {
        if (noticeState.value !== '') {
          if (noticeState.isNewEntry) {
            batchPost.push({
              metadataId: bib.get('id'),
              notice: noticeState.value,
              type
            });
          } else if (noticeState.isChecked) {
            // handles updating notices
            batchUpdate.push({
              id: existingAdvisories.find(notice => notice.get('type') === type).get('id'),
              notice: noticeState.value
            });
          } else {
            // handles removing notices
            batchDelete.push(existingAdvisories.find(notice => notice.get('type') === type).get('id'));
          }
        } else {
          errorState[type] = {
            ...this.state[type],
            detailsError: {
              id: 'error_description_required'
            }
          };
        }
      }
    });

    this.setState(
      {
        ...errorState
      },
      () => {
        if (this.isFormValid()) {
          if (batchPost.length > 0) {
            contentAdvisoriesActions.addContentAdvisories({
              contentAdvisories: batchPost,
              postSuccess
            });
          }

          if (batchUpdate.length > 0) {
            contentAdvisoriesActions.updateContentAdvisories({
              contentAdvisories: batchUpdate,
              postSuccess
            });
          }

          if (batchDelete.length > 0) {
            contentAdvisoriesActions.removeContentAdvisories({
              contentAdvisoryIds: batchDelete,
              postSuccess
            });
          }
        }
      }
    );
  }

  renderHeader() {
    return (
      <h2>
        {this.props.existingAdvisories ? (
          <FormattedMessage id="edit_content_notices" />
        ) : (
          <FormattedMessage id="add_content_notices" />
        )}
      </h2>
    );
  }

  renderContentAdvisoryFields() {
    return (
      <div className="content-advisories-ugc-form">
        {contentAdvisoryTypes.map(advisoryType => {
          const fieldErrors = this.state[advisoryType].detailsError
            ? {
                [DETAILS_ERROR_KEY]: {
                  ...this.state[advisoryType].detailsError
                }
              }
            : {};

          return (
            <ContentAdvisoryField
              key={`${advisoryType.replace(/_/g, '-')}_field`}
              className={advisoryType.replace(/_/g, '-')}
              isChecked={this.state[advisoryType].isChecked}
              notice={this.state[advisoryType].value}
              handleCheckboxChange={isChecked =>
                this.setState(state => ({
                  [advisoryType]: {
                    ...state[advisoryType],
                    isChecked,
                    detailsError: null
                  }
                }))}
              handleTextChange={value =>
                this.setState(state => ({
                  [advisoryType]: {
                    ...state[advisoryType],
                    value,
                    detailsError: null
                  }
                }))}
              label={this.props.intl.formatMessage({
                id: `${advisoryType}_advisory_type`
              })}
              fieldErrors={fieldErrors}
            />
          );
        })}
      </div>
    );
  }

  render() {
    const {
      bib,
      shelfItem,
      shelfTransactions,
      existingAdvisories,
      shelfActions,
      contentAdvisoriesTransactions,
      handleClose
    } = this.props;
    const isUpdating =
      existingAdvisories &&
      existingAdvisories.some(contentAdvisory =>
        contentAdvisoriesTransactions.getIn([contentAdvisory.get('id'), 'isUpdating'], false)
      );
    const isRemoving =
      existingAdvisories &&
      existingAdvisories.some(contentAdvisory =>
        contentAdvisoriesTransactions.getIn([contentAdvisory.get('id'), 'isRemoving'], false)
      );
    const isAdding = contentAdvisoryTypes.some(type =>
      contentAdvisoriesTransactions.getIn([bib.get('id') + type, 'isAdding'], false)
    );
    const isSavingContentAdvisories = isUpdating || isRemoving || isAdding;
    const isSaveButtonDisabled = contentAdvisoryTypes.every(
      type => !this.state[type].isChecked && this.state[type].value === ''
    );

    return (
      <div>
        <BaseUGCEditor
          bib={bib}
          className="cp-content-advisory-ugc-editor"
          renderHeader={this.renderHeader}
          handleSave={this.handleSave}
          handleClose={handleClose}
          isSaving={isSavingContentAdvisories}
          showPrivacyAlert
          shelfItem={shelfItem}
          shelfTransactions={shelfTransactions}
          shelfActions={shelfActions}
          saveButtonDisabled={isSaveButtonDisabled}
        >
          {this.renderContentAdvisoryFields()}
        </BaseUGCEditor>
      </div>
    );
  }
}

ContentAdvisoryUGCEditor.propTypes = {
  bib: bibShape.isRequired,
  existingAdvisories: ImmutablePropTypes.listOf(ugcContentAdvisoryShape),
  shelfItem: shelfItemShape,
  handleClose: PropTypes.func.isRequired,
  shelfTransactions: ImmutablePropTypes.map.isRequired,
  shelfActions: PropTypes.objectOf(PropTypes.func).isRequired,
  contentAdvisoriesActions: PropTypes.objectOf(PropTypes.func).isRequired,
  contentAdvisoriesTransactions: ImmutablePropTypes.map.isRequired
};

export default injectIntl(ContentAdvisoryUGCEditor);
